import { useState } from 'react';

interface Project {
  category: string;
  title: string;
  image: string;
  description?: string;
  gallery?: string[];
}

interface ModalState {
  open: boolean;
  project: Project | null;
  activeIndex: number;
}

export default function Projects() {
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [modal, setModal] = useState<ModalState>({ open: false, project: null, activeIndex: 0 });

  const filters = ['Todos', 'Construcción', 'Mantenimiento'];

  const projects: Project[] = [
    {
      category: 'Construcción',
      title: 'Tinglado Metálico en Casa Habitación en Saltillo',
      image: 'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/e7a5340e2ed434dc4acd49632affde14.jpeg',
      description:
        'Estructura metálica ligera con cubierta tipo pérgola para área residencial/industrial, optimizada para iluminación natural y durabilidad.',
      gallery: [
        'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/e7a5340e2ed434dc4acd49632affde14.jpeg',
        'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/85d5d63e0cbc4b45f7c7d1fbd0a6ea3d.jpeg',
        'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/db2ef48c5c8e835135e59cb067b87b09.jpeg',
      ],
    },
    {
      category: 'Construcción',
      title: 'Construcción y Adecuación de Locales Comerciales (Tiendas SIX)',
      image: 'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/a9559c967879e842a7aba4446ae2f1c8.jpeg',
      description:
        'Construcción, habilitación y adecuación integral de locales comerciales para la cadena de tiendas SIX, incluyendo obra civil, acabados e instalaciones.',
      gallery: [
        'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/a9559c967879e842a7aba4446ae2f1c8.jpeg',
        'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/830ce7ca3f056e47eee81f2bde85c4d8.jpeg',
        'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/ffe947d3f601c32f59666fa619e8fd53.jpeg',
      ],
    },
    {
      category: 'Mantenimiento',
      title: 'Instalación y mantenimiento de accesos industriales',
      image: 'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/02e5af3e9f30bb4b041fbfa36f170541.jpeg',
      description:
        'Suministro, instalación y mantenimiento correctivo de cortinas metálicas enrollables, accesos de andén y puertas industriales en bodegas, locales comerciales, plantas y almacenes. Soluciones integrales de acceso seguro y duradero.',
      gallery: [
        'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/02e5af3e9f30bb4b041fbfa36f170541.jpeg',
        'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/25efde044c67c591608e6eea7b47ee72.jpeg',
        'https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/552dc0c0b1a68b848a0699b000afed24.jpeg',
      ],
    },
  ];

  const filteredProjects =
    activeFilter === 'Todos'
      ? projects
      : projects.filter((project) => project.category === activeFilter);

  const openModal = (project: Project) => {
    setModal({ open: true, project, activeIndex: 0 });
  };

  const closeModal = () => {
    setModal({ open: false, project: null, activeIndex: 0 });
  };

  const goToImage = (index: number) => {
    setModal((prev) => ({ ...prev, activeIndex: index }));
  };

  const prevImage = () => {
    if (!modal.project?.gallery) return;
    setModal((prev) => ({
      ...prev,
      activeIndex: (prev.activeIndex - 1 + (modal.project?.gallery?.length ?? 1)) % (modal.project?.gallery?.length ?? 1),
    }));
  };

  const nextImage = () => {
    if (!modal.project?.gallery) return;
    setModal((prev) => ({
      ...prev,
      activeIndex: (prev.activeIndex + 1) % (modal.project?.gallery?.length ?? 1),
    }));
  };

  return (
    <section id="proyectos" className="relative py-24 overflow-hidden">
      {/* Imagen de fondo */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/4ebe2c70aa72ef2c6dfc01f370e8f72f/2c7c1428382a0a7f5f303172178ee1e5.webp"
          alt="Proyectos realizados"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Encabezado */}
        <div className="text-center mb-12">
          <h2 className="text-5xl lg:text-6xl font-heading font-bold text-white mb-4">
            Proyectos Realizados
          </h2>
          <p className="text-xl text-white/70">Proyectos recientes ejecutados con calidad y experiencia</p>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeFilter === filter
                  ? 'bg-[#C9A86A] text-[#1A1A2E]'
                  : 'bg-transparent border-2 border-white/40 text-white/70 hover:border-[#C9A86A] hover:text-white'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Grid de proyectos */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-2xl cursor-pointer"
              onClick={() => openModal(project)}
            >
              {/* Imagen cuadrada 1:1 */}
              <div className="w-full aspect-square overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
                />
              </div>

              {/* Overlay al hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute top-4 right-4 flex items-center gap-2">
                  <span className="px-4 py-2 bg-[#C9A86A] text-[#1A1A2E] text-sm font-semibold rounded-full">
                    {project.category}
                  </span>
                  {project.gallery && project.gallery.length > 1 && (
                    <span className="px-3 py-2 bg-black/60 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                      <i className="ri-image-2-line text-sm"></i>
                      {project.gallery.length}
                    </span>
                  )}
                </div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-white text-xl font-bold mb-2 leading-snug">{project.title}</h3>
                  {project.description && (
                    <p className="text-white/75 text-sm mb-3 leading-relaxed line-clamp-2">
                      {project.description}
                    </p>
                  )}
                  <button className="px-5 py-2 border-2 border-white text-white rounded-full text-sm font-semibold hover:bg-white hover:text-[#1A1A2E] transition-all whitespace-nowrap cursor-pointer">
                    Ver detalles
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de galería */}
      {modal.open && modal.project && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={closeModal}
        >
          <div
            className="relative w-full max-w-4xl mx-4 bg-[#1a1a1a] rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del modal */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <span className="px-3 py-1 bg-[#C9A86A] text-[#1A1A2E] text-xs font-semibold rounded-full mr-3">
                  {modal.project.category}
                </span>
                <span className="text-white/50 text-sm">
                  {modal.project.gallery
                    ? `${modal.activeIndex + 1} / ${modal.project.gallery.length}`
                    : '1 / 1'}
                </span>
              </div>
              <button
                onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-white text-xl"></i>
              </button>
            </div>

            {/* Imagen principal */}
            <div className="relative w-full aspect-square bg-black">
              <img
                src={
                  modal.project.gallery
                    ? modal.project.gallery[modal.activeIndex]
                    : modal.project.image
                }
                alt={modal.project.title}
                className="w-full h-full object-contain"
              />

              {/* Flechas de navegación */}
              {modal.project.gallery && modal.project.gallery.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 hover:bg-[#C9A86A] text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-xl"></i>
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 hover:bg-[#C9A86A] text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-xl"></i>
                  </button>
                </>
              )}
            </div>

            {/* Info y miniaturas */}
            <div className="px-6 py-5">
              <h3 className="text-white text-xl font-bold mb-2">{modal.project.title}</h3>
              {modal.project.description && (
                <p className="text-white/65 text-sm leading-relaxed mb-4">
                  {modal.project.description}
                </p>
              )}

              {/* Thumbnails */}
              {modal.project.gallery && modal.project.gallery.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {modal.project.gallery.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => goToImage(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                        idx === modal.activeIndex
                          ? 'border-[#C9A86A]'
                          : 'border-transparent opacity-50 hover:opacity-80'
                      }`}
                    >
                      <img src={img} alt={`Vista ${idx + 1}`} className="w-full h-full object-cover object-center" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
